import type { WeatherNow } from '@shared/types';
import { clockFromIso, formatLocalHm, formatSpeedMph, formatTempDeg, formatTempF, sunClocks } from '@/lib/formatting';

function formatHour(iso: string): string {
  const match = iso.match(/T(\d{2}:\d{2})/) ?? iso.match(/^(\d{2}:\d{2})/);
  const label = match?.[1] ? formatLocalHm(match[1]) : clockFromIso(iso);
  return label.replace(':00 ', ' ');
}

function uvLabel(uv: number): { label: string; color: string } {
  if (uv >= 8) return { label: 'Very high', color: '#b42318' };
  if (uv >= 6) return { label: 'High', color: '#b88c3c' };
  if (uv >= 3) return { label: 'Moderate', color: '#122644' };
  return { label: 'Low', color: '#2f6b3a' };
}

export function WeatherInfographic({ weather }: { weather: WeatherNow }) {
  const hourly = weather.hourly.slice(0, 12);
  const temps = hourly.map((h) => h.tempC);
  const maxTemp = Math.max(...temps, weather.highC ?? weather.tempC);
  const minTemp = Math.min(...temps, weather.lowC ?? weather.tempC);
  const range = Math.max(1, maxTemp - minTemp);
  const uv = uvLabel(weather.uvIndex);
  const nowF = formatTempF(weather.tempC);
  const sun = sunClocks(weather.sunrise, weather.sunset);

  return (
    <section className="weather-info card" role="group" aria-label={`Weather in Carvoeiro, ${nowF}`}>
      <div className="weather-info__top">
        <div className="weather-info__temp">
          <p className="weather-info__place">{weather.locationLabel}</p>
          <div className="weather-info__temp-value">{nowF}</div>
          <div className="weather-info__temp-meta">
            <div className="weather-info__conditions">{weather.summary}</div>
            <div className="weather-info__feels">Feels like {formatTempDeg(weather.feelsLikeC)}</div>
            <div className="weather-info__hilo">
              H {formatTempDeg(weather.highC ?? weather.tempC)} · L {formatTempDeg(weather.lowC ?? weather.tempC)}
            </div>
          </div>
        </div>
      </div>

      {hourly.length > 0 && (
        <div className="weather-info__hourly" aria-label="Hourly forecast">
          {hourly.map((h) => {
            const heightPct = ((h.tempC - minTemp) / range) * 60 + 20;
            return (
              <div key={h.time} className="weather-info__hour">
                <div className="weather-info__hour-temp">{formatTempDeg(h.tempC)}</div>
                <div className="weather-info__hour-bar-wrap">
                  <div className="weather-info__hour-bar" style={{ height: `${heightPct}%` }} />
                </div>
                <div className="weather-info__hour-time">{formatHour(h.time)}</div>
              </div>
            );
          })}
        </div>
      )}

      {sun.sunrise || sun.sunset ? (
        <div className="weather-info__sun-row">
          {sun.sunrise ? (
            <div className="weather-info__sun-item">
              <span className="weather-info__sun-icon" aria-hidden>
                ↑
              </span>
              <div>
                <div className="weather-info__sun-label">Sunrise</div>
                <div className="weather-info__sun-time">{sun.sunrise}</div>
              </div>
            </div>
          ) : null}
          {sun.sunset ? (
            <div className="weather-info__sun-item">
              <span className="weather-info__sun-icon weather-info__sun-icon--set" aria-hidden>
                ↓
              </span>
              <div>
                <div className="weather-info__sun-label">Sunset</div>
                <div className="weather-info__sun-time">{sun.sunset}</div>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="weather-info__stats">
        <div className="weather-info__stat">
          <div className="weather-info__stat-value">{formatSpeedMph(weather.windKmh)}</div>
          <div className="weather-info__stat-label">Wind</div>
        </div>
        <div className="weather-info__stat">
          <div className="weather-info__stat-value">{Math.round(weather.humidity)}%</div>
          <div className="weather-info__stat-label">Humidity</div>
        </div>
        <div className="weather-info__stat">
          <span className="weather-info__stat-uv" style={{ background: uv.color }}>
            {weather.uvIndex.toFixed(0)}
          </span>
          <div>
            <div className="weather-info__stat-value">{uv.label}</div>
            <div className="weather-info__stat-label">UV Index</div>
          </div>
        </div>
      </div>
    </section>
  );
}
